import { createLogger } from './logger';

export interface ErrorTrackerProvider {
  captureException(err: Error, context?: Record<string, unknown>): void;
  logMessage(
    message: string,
    context?: Record<string, unknown>,
    level?: 'info' | 'warn' | 'error',
  ): void;
}

// Fallback logger with trace level so nothing gets filtered — the error
// tracker is a reporting channel, not a general logging system.
const fallbackLogger = createLogger({ level: 'trace' });

let provider: ErrorTrackerProvider | undefined;
let initialized = false;

function warnUninitialized(method: string): void {
  const isDev =
    typeof process !== 'undefined' &&
    process.env['NODE_ENV'] !== 'production';

  if (isDev) {
    throw new Error(
      `errorTracker.${method}() called before setProvider() — call setProvider() at app startup`,
    );
  }

  fallbackLogger.warn(`errorTracker.${method}() called before setProvider() — error tracker not initialized`, {
    hint: 'Call errorTracker.setProvider() at startup, even with undefined to explicitly disable',
  });
}

export const errorTracker = {
  /**
   * Register an external provider (e.g., Sentry) to handle error reporting.
   * Pass `undefined` to explicitly disable (no-op mode). Call sites remain
   * unchanged — only the backend implementation swaps.
   */
  setProvider(p: ErrorTrackerProvider | undefined): void {
    provider = p;
    initialized = true;
  },

  /** Reset to uninitialized state. For test cleanup only. */
  reset(): void {
    provider = undefined;
    initialized = false;
  },

  captureException(err: Error, context?: Record<string, unknown>): void {
    if (!initialized) {
      warnUninitialized('captureException');
    }
    if (provider) {
      provider.captureException(err, context);
      return;
    }
    fallbackLogger.error(err.message, {
      errorName: err.name,
      stack: err.stack,
      ...context,
    });
  },

  logMessage(
    message: string,
    context?: Record<string, unknown>,
    level: 'info' | 'warn' | 'error' = 'error',
  ): void {
    if (!initialized) {
      warnUninitialized('logMessage');
    }
    if (provider) {
      provider.logMessage(message, context, level);
      return;
    }
    fallbackLogger[level](message, context);
  },
} as const;
